import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import Popup from 'components/shared/Popups/Popup/Index';
import ConfirmPopup from 'components/shared/Popups/Confirm/Index';
import {
  ISharedForm,
  ISharedImagesPopup
} from 'core/interface/sharedImagesPopup';
import Image from 'components/image/Image';
import ActionButton from 'components/ActionButton/Index';
import { Form, Formik, FormikHelpers } from 'formik';
import FormField from 'components/FormField/Index';
import { InputType } from 'core/enum/inputType';
import { initialValues, validation } from './form';
import Button from 'components/Button/Index';
import { AccountServce } from 'services/upload/file-upload';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useSnackbar } from 'notistack';

import './Index.scss';

// File size limit in bytes (5MB)
const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

const SharedImagesPopup: FC<ISharedImagesPopup> = ({
  t,
  images,
  handleClose
}): JSX.Element => {
  const fileRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [imagesPreview, setImagesPreview] = useState<Array<any> | null>(null);
  const [isPrev, setIsPrev] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<any>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<any>(null);
  const user = useSelector((state: RootState) => state.user).user;
  const uploadedFiles = useSelector((state: RootState) => state.file.files);

  const validateFiles = (fileList: FileList): File[] => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(fileList).forEach((file) => {
      // Check file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        errors.push(
          t(
            'File {{fileName}} has unsupported format. Only JPG and PNG files are allowed.',
            { fileName: file.name }
          )
        );
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        errors.push(
          t(
            'File {{fileName}} is too large ({{fileSize}}MB). Maximum file size is 5MB.',
            {
              fileName: file.name,
              fileSize: fileSizeInMB
            }
          )
        );
        return;
      }

      validFiles.push(file);
    });

    // Show error messages
    errors.forEach((error) => {
      enqueueSnackbar(error, {
        variant: 'error'
      });
    });

    return validFiles;
  };

  const handleRemoveImage = (item: any) => {
    if (!user) {
      return;
    }
    if (!item.isItem) {
      // Local preview — no API call, remove directly
      setImagesPreview((prevState) =>
        prevState
          ? prevState.filter((_, index) => index !== item.id)
          : prevState
      );
      setIsPrev(false);
    } else {
      // Uploaded image — confirm before deleting
      setPendingDeleteItem(item);
      setDeleteConfirmOpen(true);
    }
  };

  const confirmDelete = (): void => {
    if (pendingDeleteItem && user) {
      AccountServce.removeUploadedImages(user.candidate_id, pendingDeleteItem.id);
      handleClose();
    }
    setDeleteConfirmOpen(false);
    setPendingDeleteItem(null);
  };

  const cancelDelete = (): void => {
    setDeleteConfirmOpen(false);
    setPendingDeleteItem(null);
  };

  useEffect(() => {
    setImagesPreview(uploadedFiles);
  }, [uploadedFiles]);

  const handleImageChosen = (files: FileList): void => {
    // First check if adding these files would exceed the 5 file limit (before validation)
    // Only count images from props (already uploaded to API) + currently selected preview files
    const existingImagesCount = images?.length || 0;
    const currentPreviewCount = imagesPreview?.length || 0;

    // Don't double count if imagesPreview contains uploadedFiles
    const actualPreviewCount =
      Array.isArray(imagesPreview) &&
      Array.isArray(uploadedFiles) &&
      imagesPreview.length === uploadedFiles.length &&
      imagesPreview.every((item, index) => item === uploadedFiles[index])
        ? 0
        : currentPreviewCount;

    const currentFileCount = existingImagesCount + actualPreviewCount;
    const totalFiles = currentFileCount + files.length;

    if (totalFiles > 5) {
      enqueueSnackbar(
        t('You can upload maximum 5 images. Please remove some images first.'),
        {
          variant: 'error'
        }
      );
      return;
    }

    // Then validate the files for size and type
    const validFiles = validateFiles(files);

    if (validFiles.length === 0) {
      return;
    }

    const imageUrls = validFiles.map((file) => URL.createObjectURL(file));
    const fileList = new DataTransfer();
    validFiles.forEach((file) => fileList.items.add(file));

    setFiles(fileList.files);
    setImagesPreview((prevState) =>
      prevState ? [...prevState, ...imageUrls] : imageUrls
    );
    setIsPrev(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    handleImageChosen(e.dataTransfer.files);
  };

  const handleSubmit = (
    values: ISharedForm,
    actions: FormikHelpers<ISharedForm>
  ) => {
    uploadBrowsedFiles(values.title);
    return;
  };

  async function uploadBrowsedFiles(title: string) {
    setLoading(true);
    try {
      const formData = new FormData();

      formData.append('title', title);

      const fileListArray = Array.from(files);

      if (fileListArray.length > 0) {
        fileListArray.forEach((file: any) => {
          formData.append('uploaded_file', file, file.name);
        });
      }

      if (user && formData) {
        await AccountServce.uploadImages(user.candidate_id, formData);
        enqueueSnackbar(t('Images uploaded successfully!'), {
          variant: 'success'
        });
        handleClose();
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      enqueueSnackbar(t('Failed to upload images. Please try again.'), {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  }

  const chooseFile = () => {
    if (fileRef.current) {
      (fileRef.current as HTMLInputElement).click();
    }
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>): void => {
    const { files } = e.target;

    if (files) handleImageChosen(files);
  };

  return (
    <div className="shared">
      {deleteConfirmOpen && (
        <Popup
          className="popUp"
          title={t('Delete Image')}
          onClose={cancelDelete}
        >
          <ConfirmPopup
            description={t('Are you sure you want to delete this image?')}
            onApprove={confirmDelete}
            onCancel={cancelDelete}
            approveTitle={t('Yes, Delete')}
            cancelTitle={t('Cancel')}
            t={t}
          />
        </Popup>
      )}
      <div className="images">
        {images &&
          images.map((item, index) => (
            <div key={index} className="image">
              <ActionButton onClick={() => handleRemoveImage(item)} />
              <Image key={index} url={item.image} />
            </div>
          ))}
        {imagesPreview &&
          imagesPreview.map((item: any, index) => (
            <div key={index} className="image">
              <span
                className="trash-shared"
                onClick={() =>
                  handleRemoveImage({
                    isItem: !!item.uploaded_file,
                    id: item.id ?? index
                  })
                }
              >
                <Image name="shared-remove.svg" width={36} height={36} />
              </span>
              <Image
                key={index}
                url={item?.uploaded_file ?? item}
                className="shared-images"
              />
            </div>
          ))}
      </div>
      <div className={`about-side`}>
        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`drag-and-drop ${
            files?.length >= 5 ||
            isPrev ||
            (uploadedFiles && uploadedFiles?.length >= 5)
              ? 'disabled'
              : ''
          }`}
        >
          <div className="texts">
            <Image
              className="upload"
              name="upload-file.svg"
              width={40}
              height={40}
            />
            <span className="drag-title">
              {t('Drag and drop your files here')}{' '}
            </span>
            <span className="supported">
              {t('Files supported: JPG, PNG (Max 2MB each)')}
            </span>
            <span className="or">{t('Or')}</span>
            <Button
              t={t}
              onClick={chooseFile}
              title="Browse file"
              className="browse"
            />
          </div>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={() => validation(t)}
          onSubmit={handleSubmit}
        >
          {({ isValid }) => (
            <Form>
              <div className="field">
                <div className="label">{t('Image title')}</div>
                <FormField
                  disabled={
                    (files && files?.length >= 5) ||
                    (uploadedFiles && uploadedFiles?.length >= 5)
                  }
                  className="input"
                  placeholder={t('Title')}
                  fieldName="title"
                  type={InputType.text}
                />
              </div>
              <div className="field">
                <div className="label">{t('Description')}</div>
                <FormField
                  disabled={
                    (files && files?.length >= 5) ||
                    (uploadedFiles && uploadedFiles?.length >= 5)
                  }
                  className="input"
                  placeholder={t('Description')}
                  fieldName="description"
                  type={InputType.text}
                />{' '}
              </div>
              <Button
                disabled={
                  (files && files?.length >= 5) ||
                  (uploadedFiles && uploadedFiles?.length >= 5)
                }
                t={t}
                className={`save ${!isValid ? 'invalid-btn' : ''} ${
                  (files && files?.length >= 5) ||
                  (uploadedFiles && uploadedFiles?.length >= 5)
                    ? 'disabled'
                    : ''
                } ${!isPrev ? 'disabled' : ''}`}
                title="Save"
                type="submit"
                isLoading={loading}
              />
            </Form>
          )}
        </Formik>
      </div>
      <input
        ref={fileRef}
        className="file"
        type="file"
        accept="image/png, image/jpeg"
        onChange={handleUpload}
        max={1}
      />
    </div>
  );
};

export default SharedImagesPopup;
